import type { Schemas } from '@ers/sdk';
import { keepPreviousData, type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, call, ifMatch, isMissingRoute } from '../../api/client';
import { apiHas } from '../../api/contract';
import { ApiError } from '../../api/errors';
import type { ApiBody, ApiQuery } from '../../components/data/apiTypes';
import { downloadFromApi, toQuery } from '../../components/data/download';
import { downloadPost } from '../../components/data/postDownload';
import { useToast } from '../../components/ui/Toast';
import { errorText } from '../../components/ui/ServerError';
import { useSession } from '../../app/session';

/*
 * Фінанси робіт (модуль finance, М13): параметри фінансового директора з датою дії, довідник одержувачів,
 * фінансовий звіт замовлення (план/факт, ЧЗ, маржа), реєстр витрат і погодження гарантованих витрат,
 * прибутковість, «правило 100 USD», прогноз надходжень і дебіторка, оплата праці, обмін CSV з pravdopfinance.
 * Фінансові дані — лише з областю finance:read; змінює їх фінансова роль (бізнес-роль із налаштувань модуля
 * або власник організації). Юрист без finance:read бачить лише свої запити причин «правила 100 USD» (без сум).
 * Типи — з контракту (@ers/sdk), ключі кешу — під префіксом ['finance'].
 */

export type FinanceMe = Schemas['FinanceMe'];
export type FinanceSettings = Schemas['FinanceSettings'];
export type FinanceSettingsValues = Schemas['FinanceSettingsValues'];
export type FinanceParamsVersion = Schemas['FinanceParamsVersion'];
export type FinanceRecipient = Schemas['FinanceRecipient'];
export type FinanceExpense = Schemas['FinanceExpense'];
export type FinanceExpenseChange = Schemas['FinanceExpenseChange'];
export type FinanceExpenseList = Schemas['FinanceExpenseList'];
export type FinanceExpenseResult = Schemas['FinanceExpenseResult'];
export type FinanceMatterReport = Schemas['FinanceMatterReport'];
export type FinanceFigures = Schemas['FinanceFigures'];
export type FinancePlan = Schemas['FinancePlan'];
export type FinanceProfitability = Schemas['FinanceProfitability'];
export type FinanceRule100List = Schemas['FinanceRule100List'];
export type FinanceRule100Requests = Schemas['FinanceRule100Requests'];
export type FinanceForecast = Schemas['FinanceForecast'];
export type FinanceReceivables = Schemas['FinanceReceivables'];
export type FinancePayroll = Schemas['FinancePayroll'];
export type FinanceBonusInputs = Schemas['FinanceBonusInputs'];
export type FinanceExchangeRun = Schemas['FinanceExchangeRun'];
export type FinanceImportResult = Schemas['FinanceImportResult'];

export type ExpenseKind = FinanceExpense['kind'];
export type ParamsBody = ApiBody<'/api/v1/finance/params', 'post'>;
export type SettingsPatch = ApiBody<'/api/v1/finance/settings', 'patch'>;
export type RecipientBody = ApiBody<'/api/v1/finance/recipients', 'post'>;
export type RecipientPatch = ApiBody<'/api/v1/finance/recipients/{id}', 'patch'>;
export type ExpenseBody = ApiBody<'/api/v1/finance/matters/{id}/expenses', 'post'>;
export type ExpensePatch = ApiBody<'/api/v1/finance/expenses/{id}', 'patch'>;
export type ProfitQuery = ApiQuery<'/api/v1/finance/profitability', 'get'>;
export type Rule100Query = ApiQuery<'/api/v1/finance/rule100', 'get'>;
export type ForecastQuery = ApiQuery<'/api/v1/finance/forecast', 'get'>;
export type ReceivablesQuery = ApiQuery<'/api/v1/finance/receivables', 'get'>;
export type ImportBody = ApiBody<'/api/v1/finance/exchange/import', 'post'>;
export type ExportBody = ApiBody<'/api/v1/finance/exchange/export', 'post'>;

/** Ключі кешу: усе під ['finance'] — подія потоку «finance.*» оновлює модуль цілком. */
export const financeKeys = {
  all: ['finance'] as const,
  me: ['finance', 'me'] as const,
  settings: ['finance', 'settings'] as const,
  params: (archived: boolean) => ['finance', 'params', { archived }] as const,
  recipients: (archived: boolean) => ['finance', 'recipients', { archived }] as const,
  matter: (id: string) => ['finance', 'matter', id] as const,
  expenses: (id: string, cancelled: boolean) => ['finance', 'matter', id, 'expenses', { cancelled }] as const,
  changes: (q: Record<string, unknown>) => ['finance', 'changes', q] as const,
  profitability: (q: Record<string, unknown>) => ['finance', 'profitability', q] as const,
  rule100: (q: Record<string, unknown>) => ['finance', 'rule100', q] as const,
  rule100Requests: (closed: boolean) => ['finance', 'rule100-requests', { closed }] as const,
  forecast: (q: Record<string, unknown>) => ['finance', 'forecast', q] as const,
  receivables: (q: Record<string, unknown>) => ['finance', 'receivables', q] as const,
  payroll: (month: string) => ['finance', 'payroll', month] as const,
  bonus: (month: string, userId: string) => ['finance', 'bonus', month, userId] as const,
  runs: (q: Record<string, unknown>) => ['finance', 'runs', q] as const,
};

/** Відмова, за якої розділ не показується: маршрут не підключено, немає прав або сеансу. */
function hidesModule(e: unknown): boolean {
  return isMissingRoute(e) || (e instanceof ApiError && (e.status === 401 || e.status === 403));
}

// ——— Доступ ———

/** Налаштування модуля (лише з finance:read). Водночас — перевірка, що модуль підключено на сервері. */
export function useFinanceSettings(enabled = true) {
  const { can } = useSession();
  return useQuery({
    queryKey: financeKeys.settings,
    enabled: enabled && can('finance:read') && apiHas('GET /api/v1/finance/settings'),
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: () => call(api.GET('/api/v1/finance/settings')),
  });
}

/**
 * Мої права у фінансах (GET /finance/me, область legal:read — доступний і юристу): чи бачу суми, чи маю
 * фінансову роль, чи погоджую гарантовані витрати, чи вношу причини «100 USD» і скільки запитів чекають на мене.
 * Правила ті самі, що в перевірках змін на сервері, — вебзастосунок їх не дублює.
 */
export function useFinanceMe(enabled = true) {
  const { can } = useSession();
  return useQuery({
    queryKey: financeKeys.me,
    enabled: enabled && can('legal:read') && apiHas('GET /api/v1/finance/me'),
    staleTime: 60_000,
    retry: false,
    queryFn: () => call(api.GET('/api/v1/finance/me')),
  });
}

/**
 * Мої запити причин «правила 100 USD» (без сум; потрібна лише legal:read). closed — також надані й прийняті
 * причини, щоб юрист бачив результат розгляду.
 */
export function useRule100Requests(enabled = true, closed = false) {
  const { can } = useSession();
  return useQuery({
    queryKey: financeKeys.rule100Requests(closed),
    enabled: enabled && can('legal:read') && apiHas('GET /api/v1/finance/rule100/requests'),
    staleTime: 60_000,
    retry: false,
    queryFn: () => call(api.GET('/api/v1/finance/rule100/requests', { params: { query: { includeClosed: closed || undefined } } })),
  });
}

export type FinanceActor = {
  /** Бачить суми, прибутковість і фінансові звіти (finance:read, модуль відповідає). */
  finance: boolean;
  /** Фінансова роль: параметри, витрати, прийняття причин (бізнес-роль із налаштувань або власник організації). */
  manager: boolean;
  /** Погоджує зміни гарантованих витрат (крім власних запитів). */
  canApproveGuaranteed: boolean;
  /** Вносить причини «100 USD» не лише у власних запитах (роль із налаштувань або фінансова роль). */
  canGiveRule100Reasons: boolean;
  /** Відкриті запити причин «100 USD», адресовані мені. */
  openRule100Requests: number;
  /** Бізнес-ролі фінансової ролі — для пояснень «лише перегляд». */
  managerRoleCodes: string[];
  /** Може подавати запити на зміну гарантованих витрат і вносити причини у власних запитах. */
  legalWrite: boolean;
  settings: FinanceSettingsValues | null;
  loading: boolean;
};

/**
 * Хто діє у фінансах — з відповіді сервера (GET /finance/me). Налаштування модуля (лише з finance:read)
 * потрібні сторінкам для категорій витрат, фіксації гарантованих і роздільника CSV.
 */
export function useFinanceActor(): FinanceActor {
  const { can } = useSession();
  const me = useFinanceMe();
  const q = useFinanceSettings(me.data?.finance ?? can('finance:read'));
  const values = q.data?.values ?? null;
  const finance = !!me.data?.finance && !!q.data;
  return {
    finance,
    manager: finance && !!me.data?.manager,
    canApproveGuaranteed: finance && !!me.data?.canApproveGuaranteed,
    canGiveRule100Reasons: !!me.data?.canGiveRule100Reasons,
    openRule100Requests: me.data?.openRule100Requests ?? 0,
    managerRoleCodes: me.data?.managerRoleCodes ?? values?.managerRoleCodes ?? [],
    legalWrite: can('legal:write'),
    settings: values,
    loading: me.isLoading || q.isLoading,
  };
}

/**
 * Доступ до розділу «Фінанси робіт» у навігації (за GET /finance/me): з доступом до фінансів — завжди;
 * працівник без нього — коли йому адресовано запити причин «правила 100 USD» (відкриті або вже розглянуті).
 * Поки відповіді немає — false (вимога ModuleDefinition.useAccess).
 */
// Працівник, якому адресували запит причини, лишається в розділі до кінця сеансу вкладки: інакше після
// внесення останньої причини сторінка зникла б у нього з-під рук (розділ ховається лише після перезавантаження).
let seenRule100For: string | null = null;

export function useFinanceAvailable(): boolean {
  const { userId } = useSession();
  const me = useFinanceMe();
  const s = useFinanceSettings(!!me.data?.finance);
  // Відкритих запитів немає — перевіряємо, чи були надані чи прийняті: посилання зі «Вхідних» про результат
  // розгляду веде на /finance/rule100, і сторінка має відкритися.
  const history = useRule100Requests(!!me.data && !me.data.finance && me.data.openRule100Requests === 0 && seenRule100For !== userId, true);
  if (me.error) return !hidesModule(me.error);
  if (!me.data) return false;
  if (me.data.finance) {
    if (s.data) return true;
    return !!s.error && !hidesModule(s.error);
  }
  if (me.data.openRule100Requests > 0 || (history.data?.data.length ?? 0) > 0) seenRule100For = userId;
  return seenRule100For === userId;
}

// ——— Загальна мутація ———

export function useFinanceMutation<V, D>(
  fn: (vars: V) => Promise<D>,
  opts: { success?: string | ((d: D, v: V) => string | null); errorTitle?: string; also?: QueryKey[]; onSuccess?: (d: D, v: V) => void } = {},
) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: (d, v) => {
      void qc.invalidateQueries({ queryKey: financeKeys.all });
      for (const k of opts.also ?? []) void qc.invalidateQueries({ queryKey: k });
      const msg = typeof opts.success === 'function' ? opts.success(d, v) : opts.success;
      if (msg) toast.success(msg);
      opts.onSuccess?.(d, v);
    },
    onError: opts.errorTitle ? (e) => toast.error(opts.errorTitle!, errorText(e)) : undefined,
  });
}

// ——— Налаштування, параметри, одержувачі ———

export function usePatchSettings() {
  return useFinanceMutation(({ body, version }: { body: SettingsPatch; version: number }) => call(api.PATCH('/api/v1/finance/settings', { body, headers: ifMatch(version) })), {
    success: 'Налаштування збережено',
  });
}

export function useParamsVersions(includeArchived: boolean) {
  const { finance } = useFinanceActor();
  return useQuery({
    queryKey: financeKeys.params(includeArchived),
    enabled: finance,
    staleTime: 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/finance/params', { params: { query: { includeArchived: includeArchived ? 'true' : 'false' } } }))).data,
  });
}

export function useCreateParams() {
  return useFinanceMutation((body: ParamsBody) => call(api.POST('/api/v1/finance/params', { body })), { success: (v) => `Версію параметрів з ${v.effectiveFrom} збережено` });
}

export function useArchiveParams() {
  return useFinanceMutation(({ id, reason }: { id: string; reason: string }) => call(api.POST('/api/v1/finance/params/{id}/archive', { params: { path: { id } }, body: { reason } })), {
    success: 'Версію параметрів архівовано',
  });
}

export function useRecipients(includeArchived = false) {
  const { finance } = useFinanceActor();
  return useQuery({
    queryKey: financeKeys.recipients(includeArchived),
    enabled: finance,
    staleTime: 5 * 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/finance/recipients', { params: { query: { includeArchived: includeArchived ? 'true' : 'false' } } }))).data,
  });
}

export function useCreateRecipient() {
  return useFinanceMutation((body: RecipientBody) => call(api.POST('/api/v1/finance/recipients', { body })), { success: (r) => `Одержувача «${r.name}» додано` });
}

export function usePatchRecipient() {
  return useFinanceMutation(
    ({ id, version, body }: { id: string; version: number; body: RecipientPatch }) => call(api.PATCH('/api/v1/finance/recipients/{id}', { params: { path: { id } }, body, headers: ifMatch(version) })),
    { success: (r, v) => (v.body.archived === true ? `«${r.name}» архівовано` : v.body.archived === false ? `«${r.name}» повернуто з архіву` : 'Одержувача збережено') },
  );
}

// ——— Замовлення: звіт, реєстр витрат, запити на зміну ———

export function useMatterFinance(id: string | undefined) {
  const { finance } = useFinanceActor();
  return useQuery({
    queryKey: financeKeys.matter(id ?? ''),
    enabled: !!id && finance,
    staleTime: 30_000,
    queryFn: () => call(api.GET('/api/v1/finance/matters/{id}', { params: { path: { id: id! } } })),
  });
}

export function useMatterExpenses(id: string, includeCancelled: boolean) {
  const { finance } = useFinanceActor();
  return useQuery({
    queryKey: financeKeys.expenses(id, includeCancelled),
    enabled: finance,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    queryFn: () => call(api.GET('/api/v1/finance/matters/{id}/expenses', { params: { path: { id }, query: { includeCancelled: includeCancelled ? 'true' : 'false' } } })),
  });
}

export function useCreateExpense(matterId: string) {
  return useFinanceMutation((body: ExpenseBody) => call(api.POST('/api/v1/finance/matters/{id}/expenses', { params: { path: { id: matterId } }, body })), {
    success: (r) => (r.change ? 'Запит на погодження гарантованої витрати надіслано' : 'Витрату додано до реєстру'),
  });
}

export function usePatchExpense() {
  return useFinanceMutation(
    ({ id, version, body }: { id: string; version: number; body: ExpensePatch }) => call(api.PATCH('/api/v1/finance/expenses/{id}', { params: { path: { id } }, body, headers: ifMatch(version) })),
    { success: (r) => (r.change ? 'Запит на зміну надіслано на погодження' : 'Витрату змінено') },
  );
}

export function useCancelExpense() {
  return useFinanceMutation(({ id, reason }: { id: string; reason: string }) => call(api.POST('/api/v1/finance/expenses/{id}/cancel', { params: { path: { id } }, body: { reason } })), {
    success: (r) => (r.change ? 'Запит на скасування надіслано на погодження' : 'Витрату скасовано'),
  });
}

export function useExpenseChanges(q: { status?: FinanceExpenseChange['status']; matterId?: string }, enabled = true) {
  const { finance } = useFinanceActor();
  return useQuery({
    queryKey: financeKeys.changes(q),
    enabled: enabled && finance,
    staleTime: 30_000,
    queryFn: async () => (await call(api.GET('/api/v1/finance/expense-changes', { params: { query: { ...q, limit: 100 } } }))).data,
  });
}

export function useDecideChange() {
  return useFinanceMutation(
    ({ id, decision, note }: { id: string; decision: 'approved' | 'rejected'; note?: string | null }) =>
      call(api.POST('/api/v1/finance/expense-changes/{id}/decision', { params: { path: { id } }, body: { decision, note: note ?? null } })),
    { success: (c) => (c.status === 'approved' ? `${c.matterKey}: зміну погоджено` : `${c.matterKey}: зміну відхилено`) },
  );
}

export function useWithdrawChange() {
  return useFinanceMutation((id: string) => call(api.POST('/api/v1/finance/expense-changes/{id}/withdraw', { params: { path: { id } } })), { success: 'Запит відкликано' });
}

// ——— Аналітика ———

export function useProfitability(q: ProfitQuery) {
  const { finance } = useFinanceActor();
  return useQuery({
    queryKey: financeKeys.profitability(q as Record<string, unknown>),
    enabled: finance,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: () => call(api.GET('/api/v1/finance/profitability', { params: { query: { ...q, format: 'json' } } })),
  });
}

export function downloadProfitabilityCsv(q: ProfitQuery) {
  return downloadFromApi(`/api/v1/finance/profitability${toQuery({ ...q, format: 'csv' })}`, `profitability-${q.from}_${q.to}.csv`);
}

export function useRule100List(q: Rule100Query, enabled = true) {
  const { finance } = useFinanceActor();
  return useQuery({
    queryKey: financeKeys.rule100(q as Record<string, unknown>),
    enabled: enabled && finance,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: () => call(api.GET('/api/v1/finance/rule100', { params: { query: q } })),
  });
}

export function useDetectRule100() {
  return useFinanceMutation((body: { from?: string; to?: string }) => call(api.POST('/api/v1/finance/rule100/detect', { body })), {
    success: (r) => (r.created ? `Перевірено замовлень: ${r.checked}. Нових запитів причин: ${r.created}` : `Перевірено замовлень: ${r.checked}. Нових відхилень немає`),
  });
}

export function useReviewRule100() {
  return useFinanceMutation(
    ({ matterId, decision, note }: { matterId: string; decision: 'accept' | 'return'; note?: string | null }) =>
      call(api.POST('/api/v1/finance/rule100/{id}/review', { params: { path: { id: matterId } }, body: { decision, note: note ?? null } })),
    { success: (c) => (c.status === 'accepted' ? 'Причину прийнято' : 'Причину повернуто на уточнення') },
  );
}

export function useProvideRule100Reason() {
  return useFinanceMutation(
    ({ matterId, reasonCode, reasonText, allowSensitive }: { matterId: string; reasonCode: string; reasonText: string | null; allowSensitive?: boolean }) =>
      call(api.POST('/api/v1/finance/rule100/{id}/reason', { params: { path: { id: matterId } }, body: { reasonCode, reasonText, allowSensitive } })),
    { success: 'Причину внесено — фінансова роль її розгляне' },
  );
}

export function useForecast(q: ForecastQuery) {
  const { finance } = useFinanceActor();
  return useQuery({
    queryKey: financeKeys.forecast(q as Record<string, unknown>),
    enabled: finance,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: () => call(api.GET('/api/v1/finance/forecast', { params: { query: q } })),
  });
}

export function useReceivables(q: ReceivablesQuery) {
  const { finance } = useFinanceActor();
  return useQuery({
    queryKey: financeKeys.receivables(q as Record<string, unknown>),
    enabled: finance,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: () => call(api.GET('/api/v1/finance/receivables', { params: { query: q } })),
  });
}

export function usePayroll(month: string) {
  const { finance } = useFinanceActor();
  const { can } = useSession();
  return useQuery({
    queryKey: financeKeys.payroll(month),
    enabled: finance && can('time:read'),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: () => call(api.GET('/api/v1/finance/payroll', { params: { query: { month, format: 'json' } } })),
  });
}

export function downloadPayrollCsv(month: string) {
  return downloadFromApi(`/api/v1/finance/payroll${toQuery({ month, format: 'csv' })}`, `payroll-${month}.csv`);
}

export function useBonusInputs(month: string, userId: string | null) {
  const { manager } = useFinanceActor();
  return useQuery({
    queryKey: financeKeys.bonus(month, userId ?? ''),
    enabled: manager && !!userId,
    staleTime: 60_000,
    queryFn: () => call(api.GET('/api/v1/finance/bonus-inputs', { params: { query: { month, userId: userId! } } })),
  });
}

// ——— Обмін із pravdopfinance ———

export function useExchangeRuns(direction: 'export' | 'import' | null) {
  const { finance } = useFinanceActor();
  return useQuery({
    queryKey: financeKeys.runs({ direction }),
    enabled: finance,
    staleTime: 30_000,
    queryFn: async () => (await call(api.GET('/api/v1/finance/exchange/runs', { params: { query: { limit: 50, direction: direction ?? undefined } } }))).data,
  });
}

export function useExport() {
  return useFinanceMutation((body: ExportBody) => downloadPost('/api/v1/finance/exchange/export', body, `pravdopfinance-${body.dataset}.csv`), {
    success: (r) => `Файл «${r.fileName}» збережено — запис додано в журнал обміну`,
  });
}

export function useImport() {
  return useFinanceMutation((body: ImportBody) => call(api.POST('/api/v1/finance/exchange/import', { body })), {
    also: [['legal']],
    success: (r) => (r.run.dryRun ? null : `Імпорт застосовано: рядків ${r.run.rowsApplied}`),
  });
}
